"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const ImageGallery = ({ images = [], itemClassName = "", contentClassName = "" }: { images?: (string | undefined)[], itemClassName?: string, contentClassName?: string }) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div className="mx-auto w-full select-none">
      <div className="relative mb-2 rounded-xl px-6 lg:mb-4">
        <Carousel
          opts={{
            loop: true,
            duration: 15,
          }}
          className={``}
          setApi={setApi}
        >
          <CarouselContent className={cn("ml-0 h-[45vh] max-w-full justify-start md:max-w-xl lg:h-[600px]", contentClassName)}>
            {images.filter(Boolean).map((image, index) => (
              <CarouselItem
                key={index}
                className="relative h-full w-full basis-full px-0"
              >
                <Image
                  key={index}
                  src={image!}
                  alt={"Produktbild"}
                  fill
                  sizes="800px"
                  className={`overflow-hidden rounded-xl border border-gray-100 bg-white object-contain`}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 1 && (
            <div>
              <CarouselPrevious
                className="-left-8 top-1/2 isolate scale-125 border-none bg-transparent transition-all duration-300 hover:bg-gray-100"
                icon={<ChevronLeft />}
              />
              <CarouselNext
                className="-right-8 top-1/2 isolate scale-125 border-none bg-transparent transition-all duration-300 hover:bg-gray-100"
                icon={<ChevronRight />}
              />
            </div>
          )}
        </Carousel>
      </div>
      <div className="flex select-none flex-row justify-start gap-0 overflow-auto px-4 lg:gap-0">
        {images.filter(Boolean).map((image, index) => (
          <div
            key={index}
            onClick={() => api?.scrollTo(images.indexOf(image))}
            className="first:ml:0 relative -ml-0.5 mr-0 h-20 w-20 shrink-0 border-collapse cursor-pointer last:mr-0"
          >
            <Image
              src={image!}
              alt={"Produktbild"}
              fill
              sizes="80px"
              className={`mb-2 h-full w-full cursor-pointer rounded-none border border-b-2 border-gray-100 object-contain transition-opacity duration-300 hover:border-b-orange-300 lg:h-28 lg:w-28 ${
                images.indexOf(image) + 1 === current
                  ? "border-b-2 border-b-orange-300 opacity-100"
                  : "opacity-60 hover:opacity-100"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageGallery;

export const ImageGalleryLoader = () => {
  return (
    <div className="mx-auto w-full max-w-[500px] scale-100 select-none md:scale-100">
      <div className="relative mb-2 overflow-hidden rounded-xl lg:mb-4">
        <Carousel
          opts={{
            loop: true,
            duration: 15,
          }}
          className={``}
        >
          <CarouselContent className="ml-0 h-[45vh] max-w-full justify-start md:max-w-xl lg:h-[600px]">
            <CarouselItem className="relative h-full w-full basis-full animate-pulse px-0">
              <div className="h-full w-full bg-gray-200" />
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </div>
      <div className="flex select-none flex-row justify-start gap-2 overflow-auto lg:gap-4">
        <div className="relative h-20 w-20 shrink-0 animate-pulse">
          <div className="h-full w-full bg-gray-200" />
        </div>
        <div className="relative h-20 w-20 shrink-0 animate-pulse">
          <div className="h-full w-full bg-gray-200" />
        </div>
        <div className="relative h-20 w-20 shrink-0 animate-pulse">
          <div className="h-full w-full bg-gray-200" />
        </div>
        <div className="relative h-20 w-20 shrink-0 animate-pulse">
          <div className="h-full w-full bg-gray-200" />
        </div>
      </div>
    </div>
  );
};
